import { createServerFn } from "@tanstack/react-start";
import { inArray } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { canManageMenuStructure, canManageSiteConfig } from "../auth/permissions";
import { navStructureUnchanged, slugListUnchanged } from "../navigation/menu-structure";
import { invalidateSettingsCache } from "./public.functions";
import { parseFirmContact, type FirmContact } from "../firm-contact";
import { parseSmtpSettings, parseEmailSignatureSettings, parseEnquiryNotificationSettings, parseNotifyEmailsInput, type SmtpSettings, type EmailSignatureSettings, type EnquiryNotificationSettings } from "../email/types";
import {
  parseNavMenu,
  parseFooterServiceSlugs,
  defaultHeaderMenu,
  defaultFooterMenu,
} from "../navigation/defaults";
import type { NavItem } from "../navigation/types";

const navItemSchema: z.ZodType<NavItem> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    href: z.string().optional(),
    type: z.enum(["link", "mega_services", "mega_insights", "mega_stories"]),
    children: z.array(navItemSchema).optional(),
    visible: z.boolean(),
  }),
);

const firmContactSchema = z.object({
  phone: z.string().min(1),
  email: z.string().email(),
  hours: z.string().min(1),
  address: z.object({
    line1: z.string().min(1),
    line2: z.string().min(1),
    line3: z.string().min(1),
    line4: z.string().min(1),
    lat: z.number(),
    lng: z.number(),
  }),
});

const smtpSettingsSchema = z.object({
  host: z.string(),
  port: z.number().int().min(1).max(65535),
  secure: z.boolean(),
  user: z.string(),
  password: z.string(),
  fromEmail: z.string(),
  fromName: z.string(),
});

const emailSignatureSchema = z.object({
  html: z.string(),
  includeOptOutLink: z.boolean(),
  optOutLinkText: z.string(),
});

const enquiryNotificationSettingsSchema = z.object({
  enabled: z.boolean(),
  notifyEmails: z.array(z.string().email()),
});

export const getSettings = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("settings", "read");
  const rows = await getDb().select().from(schema.siteSettings);
  return Object.fromEntries(rows.map((r) => [r.key, r.value]));
});

export const setSetting = createServerFn({ method: "POST" })
  .inputValidator(z.object({ key: z.string(), value: z.unknown() }))
  .handler(async ({ data }) => {
    await requirePermission("settings", "write");
    const db = getDb();
    await db
      .insert(schema.siteSettings)
      .values({ key: data.key, value: data.value, updatedAt: new Date() })
      .onConflictDoUpdate({
        target: schema.siteSettings.key,
        set: { value: data.value, updatedAt: new Date() },
      });
    await invalidateSettingsCache();
    return { ok: true };
  });

export const saveSiteSettings = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      googleAnalyticsId: z.string().optional(),
      siteUrl: z.string().optional(),
      firmContact: firmContactSchema.optional(),
      craftGlobalCss: z.string().optional(),
      cardAnimationsEnabled: z.boolean().optional(),
      smtpSettings: smtpSettingsSchema.optional(),
      emailSignature: emailSignatureSchema.optional(),
      enquiryNotificationSettings: enquiryNotificationSettingsSchema.optional(),
    }),
  )
  .handler(async ({ data }) => {
    const user = await requirePermission("settings", "write");
    const canManageSite = canManageSiteConfig(user.permissions);
    if (
      !canManageSite &&
      (data.smtpSettings !== undefined ||
        data.emailSignature !== undefined ||
        data.enquiryNotificationSettings !== undefined ||
        data.craftGlobalCss !== undefined)
    ) {
      throw new Error("Forbidden");
    }
    const db = getDb();
    if (data.googleAnalyticsId !== undefined) {
      await db.insert(schema.siteSettings).values({ key: "google_analytics_id", value: data.googleAnalyticsId }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: data.googleAnalyticsId, updatedAt: new Date() } });
    }
    if (data.siteUrl !== undefined) {
      await db.insert(schema.siteSettings).values({ key: "site_url", value: data.siteUrl }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: data.siteUrl, updatedAt: new Date() } });
    }
    if (data.firmContact !== undefined) {
      const contact = parseFirmContact(data.firmContact) satisfies FirmContact;
      await db.insert(schema.siteSettings).values({ key: "firm_contact", value: contact }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: contact, updatedAt: new Date() } });
    }
    if (data.craftGlobalCss !== undefined) {
      await db.insert(schema.siteSettings).values({ key: "craft_global_css", value: data.craftGlobalCss }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: data.craftGlobalCss, updatedAt: new Date() } });
    }
    if (data.cardAnimationsEnabled !== undefined) {
      await db.insert(schema.siteSettings).values({ key: "card_animations_enabled", value: data.cardAnimationsEnabled }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: data.cardAnimationsEnabled, updatedAt: new Date() } });
    }
    if (data.smtpSettings !== undefined) {
      const smtp = parseSmtpSettings(data.smtpSettings) satisfies SmtpSettings;
      await db.insert(schema.siteSettings).values({ key: "smtp_settings", value: smtp }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: smtp, updatedAt: new Date() } });
    }
    if (data.emailSignature !== undefined) {
      const signature = parseEmailSignatureSettings(data.emailSignature) satisfies EmailSignatureSettings;
      await db.insert(schema.siteSettings).values({ key: "email_signature", value: signature }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: signature, updatedAt: new Date() } });
    }
    if (data.enquiryNotificationSettings !== undefined) {
      const raw = data.enquiryNotificationSettings;
      const notifyEmails = Array.isArray(raw.notifyEmails)
        ? parseNotifyEmailsInput(raw.notifyEmails.join(", "))
        : typeof raw.notifyEmails === "string"
          ? parseNotifyEmailsInput(raw.notifyEmails)
          : [];
      const notifications = parseEnquiryNotificationSettings({
        enabled: raw.enabled,
        notifyEmails,
      }) satisfies EnquiryNotificationSettings;
      await db.insert(schema.siteSettings).values({ key: "enquiry_notification_settings", value: notifications }).onConflictDoUpdate({ target: schema.siteSettings.key, set: { value: notifications, updatedAt: new Date() } });
    }
    await invalidateSettingsCache();
    return { ok: true };
  });

export const getNavSettings = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("settings", "read");
  const rows = await getDb()
    .select()
    .from(schema.siteSettings)
    .where(inArray(schema.siteSettings.key, ["header_menu", "footer_menu", "footer_service_slugs"]));
  const map = Object.fromEntries(rows.map((r) => [r.key, r.value]));
  return {
    headerMenu: parseNavMenu(map.header_menu, defaultHeaderMenu),
    footerMenu: parseNavMenu(map.footer_menu, defaultFooterMenu),
    footerServiceSlugs: parseFooterServiceSlugs(map.footer_service_slugs),
  };
});

export const saveNavSettings = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      headerMenu: z.array(navItemSchema).optional(),
      footerMenu: z.array(navItemSchema).optional(),
      footerServiceSlugs: z.array(z.string()).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const user = await requirePermission("settings", "write");
    const canManageStructure = canManageMenuStructure(user.permissions);
    const db = getDb();

    const rows = await db
      .select()
      .from(schema.siteSettings)
      .where(inArray(schema.siteSettings.key, ["header_menu", "footer_menu", "footer_service_slugs"]));
    const current = Object.fromEntries(rows.map((r) => [r.key, r.value]));
    const currentHeader = parseNavMenu(current.header_menu, defaultHeaderMenu);
    const currentFooter = parseNavMenu(current.footer_menu, defaultFooterMenu);
    const currentSlugs = parseFooterServiceSlugs(current.footer_service_slugs);

    if (!canManageStructure) {
      if (data.headerMenu !== undefined && !navStructureUnchanged(currentHeader, data.headerMenu)) {
        throw new Error("Forbidden");
      }
      if (data.footerMenu !== undefined && !navStructureUnchanged(currentFooter, data.footerMenu)) {
        throw new Error("Forbidden");
      }
      if (
        data.footerServiceSlugs !== undefined &&
        !slugListUnchanged(currentSlugs, data.footerServiceSlugs)
      ) {
        throw new Error("Forbidden");
      }
    }

    if (data.headerMenu !== undefined) {
      await db
        .insert(schema.siteSettings)
        .values({ key: "header_menu", value: data.headerMenu, updatedAt: new Date() })
        .onConflictDoUpdate({
          target: schema.siteSettings.key,
          set: { value: data.headerMenu, updatedAt: new Date() },
        });
    }
    if (data.footerMenu !== undefined) {
      await db
        .insert(schema.siteSettings)
        .values({ key: "footer_menu", value: data.footerMenu, updatedAt: new Date() })
        .onConflictDoUpdate({
          target: schema.siteSettings.key,
          set: { value: data.footerMenu, updatedAt: new Date() },
        });
    }
    if (data.footerServiceSlugs !== undefined) {
      await db
        .insert(schema.siteSettings)
        .values({ key: "footer_service_slugs", value: data.footerServiceSlugs, updatedAt: new Date() })
        .onConflictDoUpdate({
          target: schema.siteSettings.key,
          set: { value: data.footerServiceSlugs, updatedAt: new Date() },
        });
    }
    await invalidateSettingsCache();
    return { ok: true };
  });
